import { useEffect, useState } from "react";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Star, Flame, ExternalLink, Save } from "lucide-react";

export default function LandingAdmin() {
  const [products, setProducts] = useState([]);
  const [config, setConfig] = useState({ hero_titulo: "", hero_subtitulo: "", hero_slogan: "", whatsapp: "" });
  const [savingConfig, setSavingConfig] = useState(false);

  const load = async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        api.get("/products"),
        api.get("/landing/config"),
      ]);
      setProducts(pRes.data || []);
      setConfig(cRes.data || {});
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const patchProduct = async (pid, changes) => {
    try {
      await api.patch(`/landing/products/${pid}`, changes);
      await load();
      toast.success("Actualizado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put("/landing/config", config);
      toast.success("Configuración guardada");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingConfig(false); }
  };

  return (
    <div className="space-y-6" data-testid="landing-admin-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Landing Page</h1>
          <p className="text-sm text-zinc-500">Controla qué se muestra en la tienda pública de casacompleta.com</p>
        </div>
        <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-bold text-[#E30613] hover:underline">
          <ExternalLink className="w-4 h-4" /> Ver tienda pública
        </a>
      </div>

      {/* Hero config */}
      <Card className="p-5">
        <h2 className="font-semibold text-lg mb-3">Textos del Hero</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label>Título principal</Label>
            <Input value={config.hero_titulo || ""} onChange={(e) => setConfig({ ...config, hero_titulo: e.target.value })} data-testid="config-titulo" />
          </div>
          <div className="md:col-span-2">
            <Label>Subtítulo / descripción</Label>
            <Input value={config.hero_subtitulo || ""} onChange={(e) => setConfig({ ...config, hero_subtitulo: e.target.value })} data-testid="config-subtitulo" />
          </div>
          <div>
            <Label>Etiqueta arriba del título</Label>
            <Input value={config.hero_slogan || ""} onChange={(e) => setConfig({ ...config, hero_slogan: e.target.value })} data-testid="config-slogan" />
          </div>
          <div>
            <Label>WhatsApp (con código país sin +)</Label>
            <Input value={config.whatsapp || ""} onChange={(e) => setConfig({ ...config, whatsapp: e.target.value })} placeholder="50243036012" data-testid="config-whatsapp" />
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={saveConfig} disabled={savingConfig} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-config-btn">
            <Save className="w-4 h-4 mr-2" /> {savingConfig ? "Guardando..." : "Guardar textos"}
          </Button>
        </div>
      </Card>

      {/* Productos */}
      <Card className="p-5">
        <h2 className="font-semibold text-lg mb-3">Productos en la tienda ({products.length})</h2>
        <p className="text-xs text-zinc-500 mb-4">Marca los productos que quieres que aparezcan en la landing. Elige uno como "Destacado" para que sea el producto grande principal. Configura precio de oferta y fecha de fin del cronómetro.</p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left">
              <tr>
                <th className="px-2 py-2 text-[10px] uppercase tracking-widest">Producto</th>
                <th className="px-2 py-2 text-[10px] uppercase tracking-widest text-center">Publicar</th>
                <th className="px-2 py-2 text-[10px] uppercase tracking-widest text-center">Destacado</th>
                <th className="px-2 py-2 text-[10px] uppercase tracking-widest">Precio</th>
                <th className="px-2 py-2 text-[10px] uppercase tracking-widest">Precio Oferta</th>
                <th className="px-2 py-2 text-[10px] uppercase tracking-widest">Oferta hasta</th>
                <th className="px-2 py-2 text-[10px] uppercase tracking-widest w-16">Orden</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {products.map((p) => (
                <ProductRow key={p.id} p={p} onPatch={patchProduct} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function ProductRow({ p, onPatch }) {
  const [precioOferta, setPrecioOferta] = useState(p.precio_oferta || "");
  const [ofertaHasta, setOfertaHasta] = useState(p.oferta_hasta ? p.oferta_hasta.slice(0, 16) : "");
  const [orden, setOrden] = useState(p.orden_tienda || 0);

  return (
    <tr>
      <td className="px-2 py-2">
        <div className="flex items-center gap-2">
          {p.imagen && <img src={p.imagen} alt="" className="w-10 h-10 object-cover rounded" />}
          <div>
            <div className="font-medium text-sm" translate="no">{p.nombre}</div>
            <div className="text-[10px] text-zinc-500">{p.categoria || "—"}</div>
          </div>
        </div>
      </td>
      <td className="px-2 py-2 text-center">
        <Switch checked={!!p.publicar_en_tienda} onCheckedChange={(v) => onPatch(p.id, { publicar_en_tienda: v })} data-testid={`toggle-publicar-${p.id}`} />
      </td>
      <td className="px-2 py-2 text-center">
        <button
          onClick={() => onPatch(p.id, { es_destacado: !p.es_destacado })}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold ${p.es_destacado ? "bg-[#E30613] text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
          data-testid={`toggle-destacado-${p.id}`}
        >
          <Star className={`w-3 h-3 ${p.es_destacado ? "fill-white" : ""}`} />
          {p.es_destacado ? "SÍ" : "No"}
        </button>
      </td>
      <td className="px-2 py-2 tabular-nums">{formatMoney(p.precio)}</td>
      <td className="px-2 py-2">
        <div className="flex items-center gap-1">
          <input
            type="number"
            step="0.01"
            value={precioOferta}
            onChange={(e) => setPrecioOferta(e.target.value)}
            onBlur={() => onPatch(p.id, { precio_oferta: precioOferta === "" ? 0 : Number(precioOferta) })}
            className="w-24 h-8 px-2 border border-zinc-300 rounded text-sm"
            placeholder="Sin oferta"
            data-testid={`precio-oferta-${p.id}`}
          />
          {p.precio_oferta && p.precio_oferta < p.precio && (
            <Flame className="w-4 h-4 text-[#E30613]" />
          )}
        </div>
      </td>
      <td className="px-2 py-2">
        <input
          type="datetime-local"
          value={ofertaHasta}
          onChange={(e) => setOfertaHasta(e.target.value)}
          onBlur={() => onPatch(p.id, { oferta_hasta: ofertaHasta })}
          className="h-8 px-2 border border-zinc-300 rounded text-sm"
          data-testid={`oferta-hasta-${p.id}`}
        />
      </td>
      <td className="px-2 py-2">
        <input
          type="number"
          value={orden}
          onChange={(e) => setOrden(e.target.value)}
          onBlur={() => onPatch(p.id, { orden_tienda: parseInt(orden) || 0 })}
          className="w-14 h-8 px-2 border border-zinc-300 rounded text-sm text-center"
          data-testid={`orden-${p.id}`}
        />
      </td>
    </tr>
  );
}
